import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";

import { firebaseApp } from "../../../../api/firebase/firestoreService";
import { logError } from "../../../../utils/customLogger";

/** Metadata persisted on the appeal record for an attached fax document. */
export interface FaxDocumentUpload {
  /** Storage path the file was uploaded to, under `appeal/fax_documents/`. */
  attachment_path: string;
  /** Original file name. */
  attachment_name: string;
  /** Tokenised download URL for viewing the file. */
  attachment_url: string;
}

/**
 * Builds the order-scoped storage path for an attached fax document.
 *
 * @input identifier - Order identifier the summary belongs to
 * @input fileName - Original file name
 * @return Storage path for the attachment
 */
const buildFaxDocumentPath = (identifier: string, fileName: string): string => {
  const uniquePrefix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}`;
  return `appeal/fax_documents/${identifier}/${uniquePrefix}_${fileName}`;
};

/**
 * Uploads a fax attachment to Firebase Storage.
 *
 * @input file - File selected in the Attach Document control
 * @input identifier - Order identifier the summary belongs to
 * @return Metadata to persist alongside the history entry
 * @raises Error when the upload fails
 */
export const uploadFaxDocument = async (
  file: File,
  identifier: string,
): Promise<FaxDocumentUpload> => {
  try {
    const storage = getStorage(firebaseApp);
    const snapshot = await uploadBytes(
      ref(storage, buildFaxDocumentPath(identifier, file.name)),
      file,
    );

    return {
      attachment_path: snapshot.ref.fullPath,
      attachment_name: file.name,
      attachment_url: await getDownloadURL(snapshot.ref),
    };
  } catch (error) {
    logError(error as Error, "Error uploading appeal summary fax document");
    throw error;
  }
};
